import { Component, Prop, h, Element, Build, getAssetPath } from '@stencil/core';
import { getConstant } from '../../../../../utils/constants-helpers';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

/**
 * Call-to-action type 12 component for SignNow brand user engagement
 *
 * @product signnow
 * @version 1.0.0
 * @category SignNow
 * @contentConfig Call-to-action component based on "Why choose airSlate signNow" section
 * @example <sn-call-to-action-type-12 constants='{"CTA_TYPE_12_TITLE":"be ready to get more","CTA_TYPE_12_SUBTITLE":"Why choose airSlate signNow","CTA_TYPE_12_ITEM_1_TITLE":"Free 7-day trial.","CTA_TYPE_12_ITEM_1_DESC":"Choose the plan you need and try it risk-free.","CTA_TYPE_12_ITEM_2_TITLE":"Honest pricing for full-featured plans.","CTA_TYPE_12_ITEM_2_DESC":"airSlate SignNow offers subscription plans with no overages or hidden fees at renewal.","CTA_TYPE_12_ITEM_3_TITLE":"Enterprise-grade security.","CTA_TYPE_12_ITEM_3_DESC":"airSlate SignNow helps you comply with global security standards.","CTA_TYPE_12_BUTTON_TEXT":"Start free trial","LINK__CTA_TYPE_12_BUTTON":"https://snseats.signnow.com/purchase/business_free_trial/loggedout"}'></sn-call-to-action-type-12>
 * @constant CTA_TYPE_12_TITLE_LABEL - "be ready to get more" - Section title-label
 * @constant CTA_TYPE_12_SUBTITLE - "Why choose airSlate SignNow" - Section subtitle
 * @constant CTA_TYPE_12_ITEM_1_TITLE - "Free 7-day trial." - Item 1 title
 * @constant CTA_TYPE_12_ITEM_1_DESC - "Choose the plan you need and try it risk-free." - Item 1 description
 * @constant CTA_TYPE_12_ITEM_2_TITLE - "Honest pricing for full-featured plans." - Item 2 title
 * @constant CTA_TYPE_12_ITEM_2_DESC - "airSlate SignNow offers subscription plans with no overages or hidden fees at renewal." - Item 2 description
 * @constant CTA_TYPE_12_ITEM_3_TITLE - "Enterprise-grade security." - Item 3 title
 * @constant CTA_TYPE_12_ITEM_3_DESC - "airSlate SignNow helps you comply with global security standards." - Item 3 description
 * @constant CTA_TYPE_12_BUTTON_TEXT - "Start free trial" - Button text
 * @constant LINK__CTA_TYPE_12_BUTTON - "https://snseats.signnow.com/purchase/business_free_trial/loggedout" - Link for the "Start free trial" button
 * @slot title - Main section title
 * @slot subtitle - Section subtitle
 * @slot list - List of items
 * @slot button - Action button
 */
@Component({
  tag: 'sn-call-to-action-type-12',
  styleUrl: 'index.scss',
  shadow: false,
  assetsDirs: ['.'],
})
export class SNCallToActionType12 {
  @Element() el: HTMLElement;

  /**
   * Component constants object containing all required text constants
   */
  @Prop({ mutable: true }) constants?: Record<string, string> | string;

  private animation: any;
  private loadObserver: IntersectionObserver;
  private playPauseObserver: IntersectionObserver;
  private lottieLoaded: boolean = false;

  componentWillLoad() {
    this.constants = parseJsonProp(this.constants);
  }

  componentDidLoad() {
    // Skip SSR completely - requires browser APIs and Lottie library
    if (!Build.isBrowser) return;

    // Defer all DOM operations to avoid blocking initial render
    // Use requestIdleCallback for better performance, fallback to setTimeout
    const initObserver = () => {
      // Double-check we're still in browser context
      if (!Build.isBrowser || !this.el) {
        return;
      }

      const container = this.el.querySelector('#animatedSignatureExample') as HTMLElement;
      if (!container) {
        return;
      }

      // Use IntersectionObserver to detect when component approaches viewport
      // rootMargin: '200px' - start loading when component is 200px away from viewport
      this.loadObserver = new IntersectionObserver(
        async entries => {
          for (const entry of entries) {
            if (entry.isIntersecting && !this.lottieLoaded) {
              this.lottieLoaded = true;
              await this.loadLottieAnimation(container);
              this.loadObserver.disconnect();
              break;
            }
          }
        },
        {
          rootMargin: '200px', // Start loading when component is 200px away from viewport
          threshold: 0,
        },
      );

      this.loadObserver.observe(this.el);
    };

    // Defer initialization to avoid blocking SSR or initial render
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(initObserver, { timeout: 100 });
    } else {
      setTimeout(initObserver, 0);
    }
  }

  private async loadLottieAnimation(container: HTMLElement) {
    // Skip if not in browser
    if (!Build.isBrowser) {
      return;
    }

    try {
      // Dynamic import of Lottie library (heavy library, loaded only when needed)
      // This import happens only on client side, so it's safe for SSR
      const lottieModule = await import('lottie-web/build/player/lottie_light.min.js');
      const lottie = lottieModule.default || lottieModule;

      if (!lottie) {
        console.warn('Failed to load Lottie library');
        return;
      }

      // Load animation data dynamically to avoid including it in the bundle
      // This reduces the initial bundle size significantly (544KB -> 0KB in bundle)
      // File is copied to build directory by assetsDirs: ['.'] in @Component decorator
      // Use getAssetPath to get the correct path relative to the component
      const animationPath = getAssetPath('./animationData.json');
      const animationDataResponse = await fetch(animationPath);

      if (!animationDataResponse.ok) {
        throw new Error(`Failed to load animation data: ${animationDataResponse.status} ${animationDataResponse.statusText}`);
      }

      const animationData = await animationDataResponse.json();

      container.innerHTML = '';

      this.animation = lottie.loadAnimation({
        container: container,
        renderer: 'svg',
        loop: true,
        autoplay: false,
        animationData: animationData,
      });

      // Create observer for play/pause control
      // IntersectionObserver is already checked in componentDidLoad, but double-check for safety
      if (typeof IntersectionObserver !== 'undefined') {
        this.playPauseObserver = new IntersectionObserver(
          entries => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                this.animation.play();
              } else {
                this.animation.pause();
              }
            });
          },
          { threshold: 0.5 },
        );

        this.playPauseObserver.observe(container);
      }
    } catch (error) {
      console.error('Error loading Lottie animation:', error);
    }
  }

  disconnectedCallback() {
    if (this.animation) {
      this.animation.destroy();
    }
    if (this.loadObserver) {
      this.loadObserver.disconnect();
    }
    if (this.playPauseObserver) {
      this.playPauseObserver.disconnect();
    }
  }

  render() {
    return (
      <me-section class="me-section-call-to-action-type-12" yGap="2xl" yGapLg="5xl" role="region" aria-label="Call to Action">
        <me-row yGapLg="none" yGap="none" xGapLg="none" xGap="none">
          <me-column class="page-section__column page-section__column--primary">
            <me-text class="title-illustration title-illustration__blue">{getConstant(this.constants, 'CTA_TYPE_12_TITLE_LABEL', 'be ready to get more')}</me-text>

            <me-heading class="section-subtitle" variant="2" tag="h2">
              {getConstant(this.constants, 'CTA_TYPE_12_SUBTITLE', 'Why choose airSlate SignNow')}
            </me-heading>

            <ul class="cta-list">
              <li class="cta-list__item">
                <div class="cta-list__logo cta-list__logo--roi">
                  <me-icon name="https://marketing-static.signnow.com/347/images/me/cta-type-12/logo-roi.svg" class="cta-list__logo--roi" />
                </div>
                <me-text class="cta-list__description">
                  <strong>{getConstant(this.constants, 'CTA_TYPE_12_ITEM_1_TITLE', 'Free 7-day trial. ')}</strong>{' '}
                  {getConstant(this.constants, 'CTA_TYPE_12_ITEM_1_DESC', 'Choose the plan you need and try it risk-free.')}
                </me-text>
              </li>

              <li class="cta-list__item js-ready-animate-example">
                <div class="cta-list__logo">
                  <me-icon name="https://marketing-static.signnow.com/347/images/me/cta-type-12/logo-pricing.svg" class="cta-list__logo--pricing" />
                </div>
                <me-text class="cta-list__description">
                  <strong>{getConstant(this.constants, 'CTA_TYPE_12_ITEM_2_TITLE', 'Honest pricing for full-featured plans.')}</strong>{' '}
                  {getConstant(this.constants, 'CTA_TYPE_12_ITEM_2_DESC', 'airSlate SignNow offers subscription plans with no overages or hidden fees at renewal.')}
                </me-text>
              </li>

              <li class="cta-list__item">
                <div class="cta-list__logo">
                  <me-icon name="https://marketing-static.signnow.com/347/images/me/cta-type-12/logo-security.svg" class="cta-list__logo--security" />
                </div>
                <me-text class="cta-list__description">
                  <strong>{getConstant(this.constants, 'CTA_TYPE_12_ITEM_3_TITLE', 'Enterprise-grade security.')}</strong>{' '}
                  {getConstant(this.constants, 'CTA_TYPE_12_ITEM_3_DESC', 'airSlate SignNow helps you comply with global security standards.')}
                </me-text>
              </li>
            </ul>

            <div class="free-trial free-trial__cta">
              <me-button
                href={getConstant(this.constants, 'LINK__CTA_TYPE_12_BUTTON', 'https://snseats.signnow.com/purchase/business_free_trial/loggedout')}
                class="button"
                variant="primary"
              >
                {getConstant(this.constants, 'CTA_TYPE_12_BUTTON_TEXT', 'Start free trial')}
              </me-button>
            </div>
          </me-column>

          <me-column class="page-section__column page-section__column--illustration">
            {/* Skip SSR for Lottie animation - improves SSR performance */}
            {Build.isBrowser && <div id="animatedSignatureExample"></div>}
            <me-picture
              class="lazyload"
              src="https://marketing-static.signnow.com/349/images/home-new/signature-example.svg"
              alt="illustrations signature"
              aspectRatio="1/1"
              lazy={true}
            />
          </me-column>
        </me-row>
      </me-section>
    );
  }
}
